// frontend/src/utils/intents/intentUtils.js
import axios from '../../api/axiosInstance';
import { API_BASE } from '../../api/config';
import { queryVectorMemory } from '../../api/memory';
import { debugLog } from '../logging/debugLog';

debugLog('[⚙️ Intent API BASE]', API_BASE);

const GROUP_TARGET_ALIASES = {
  children: ['kid', 'kids', 'child', 'children'],
  parents: ['parent', 'parents'],
  siblings: ['sibling', 'siblings'],
  grandparents: ['grandparent', 'grandparents'],
  friends: ['friend', 'friends', 'buddy', 'buddies', 'pal', 'pals'],
  pets: ['pet', 'pets', 'dog', 'dogs', 'cat', 'cats'],
  family: ['family'],
};

const ROLE_TARGET_ALIASES = {
  mother: ['mom', 'mother', 'mama', 'mommy'],
  father: ['dad', 'father', 'papa', 'daddy', 'pop'],
  brother: ['brother', 'brothers', 'bro'],
  sister: ['sister', 'sisters', 'sis'],
  son: ['son', 'sons'],
  daughter: ['daughter', 'daughters'],
  wife: ['wife', 'wives'],
  husband: ['husband', 'husbands'],
  grandmother: ['grandma', 'grandmother', 'grandmas', 'grandmothers', 'nana', 'granny'],
  grandfather: ['grandpa', 'grandfather', 'grandpas', 'grandfathers', 'gramps', 'pop-pop'],
  aunt: ['aunt', 'aunts'],
  uncle: ['uncle', 'uncles'],
  cousin: ['cousin', 'cousins'],
  girlfriend: ['girlfriend', 'girlfriends'],
  boyfriend: ['boyfriend', 'boyfriends'],
  'ex-girlfriend': ['ex girlfriend', 'ex-girlfriend', 'ex girlfriends', 'ex-girlfriends'],
  'ex-boyfriend': ['ex boyfriend', 'ex-boyfriend', 'ex boyfriends', 'ex-boyfriends'],
  'ex-wife': ['ex wife', 'ex-wife', 'ex wives', 'ex-wives'],
  'ex-husband': ['ex husband', 'ex-husband', 'ex husbands', 'ex-husbands'],
};

const LIST_PHRASES = [
  'add this to my list',
  'add to grocery list',
  'add to my list',
  'create a grocery list',
  'create a list',
  'here is a list',
  'here’s my list',
  'i need to buy',
  'make a grocery list',
  'make a list',
  'my grocery list is',
  'my todo list is',
  'start a grocery list',
  'start a list',
  'start my grocery list',
  'these are my tasks',
];

const REMINDER_PHRASES = [
  'remind me',
  "don't forget to",
  'note to self',
  'remember to',
  'make a note to',
  'create a reminder',
  'set a reminder'
];

const PRAYER_PHRASES = [
  'pray for me',
  'say a prayer',
  'keep me in your prayers',
  'need a prayer',
  'i’m asking for prayer',
  'please pray',
  'pray with me'
];

const COMFORT_PHRASES = [
  'i’m having a hard time',
  'i feel sad',
  "i'm struggling",
  "i'm hurting",
  'i feel alone',
  'i feel overwhelmed',
  'i feel broken',
  'i feel empty',
  'i need comfort',
  'i need support'
];

const GRATITUDE_MEMORY_PHRASES = [
  'i’m grateful',
  'i am grateful',
  'i’m thankful',
  'i am thankful',
  'i appreciate',
  'i’m so blessed'
];

const AFFIRMATION_PHRASES = [
  'i believe in myself',
  'i’ve got this',
  'i know i can do it',
  'i feel strong today',
  'i’m confident',
  'i trust myself'
];

const HUMOR_PHRASES = [
  'tell me a joke',
  'say something funny',
  'make me laugh'
];

const DREAM_PHRASES = [
  'i had a dream',
  'i dreamt that',
  'my dream was',
  'last night i dreamed',
  'remember this dream'
];

const QUOTE_PHRASES = [
  "here's a quote",
  'here is a quote',
  'i want to remember this quote',
  'save this quote',
  'remember this quote'
];

const LOSS_PHRASES = [
  'i feel so lost',
  'i miss',
  'passed away',
  'she died',
  'he died',
  'grieving',
  'my heart is broken'
];

const NAP_PHRASES = [
  'taking a nap',
  'going to nap',
  'going to take a nap',
  'i need a nap',
  "i'm going to nap",
  'i’m going to nap',
  'i am going to nap',
  'i’m going to take a nap',
  'i am going to take a nap',
  'i need rest',
  'going to lie down',
  "i'm lying down",
  'i’m lying down',
  "i'm going to sleep",
  'i am going to sleep',
  "i'm resting",
  'i am resting',
  'i’m resting'
];

const HELP_PATTERNS = [
  /^(what|how) can you help me with\??$/i,
  /^(what|how) can you do\??$/i,
  /^what are you able to help with\??$/i,
  /^what do you help with\??$/i,
  /^how do you help\??$/i,
  /^how can you support me\??$/i,
  /^what kind of things can you help me with\??$/i,
  /^what can i ask you about\??$/i,
  /^what are you here for\??$/i,
  /^how can you be of help\??$/i,
  /^how can i use you\??$/i,
];

function normalizeText(value = '') {
  return String(value || '').toLowerCase().trim();
}

function escapeRegExp(value = '') {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function messageMentionsAlias(message = '', alias = '') {
  const lower = normalizeText(message);
  const normalizedAlias = normalizeText(alias);

  if (!lower || !normalizedAlias) return false;

  const escaped = escapeRegExp(normalizedAlias);
  const pattern = new RegExp(`\\b${escaped}\\b`, 'i');
  return pattern.test(lower);
}

function resolveRecallTarget(message = '') {
  const lower = normalizeText(message);
  if (!lower) return null;

  for (const [group, aliases] of Object.entries(GROUP_TARGET_ALIASES)) {
    if (aliases.some((alias) => messageMentionsAlias(lower, alias))) {
      return {
        intent: 'recall_request',
        target: group,
        targetType: 'group',
      };
    }
  }

  for (const [role, aliases] of Object.entries(ROLE_TARGET_ALIASES)) {
    if (aliases.some((alias) => messageMentionsAlias(lower, alias))) {
      return {
        intent: 'recall_request',
        target: role,
        targetType: 'role',
      };
    }
  }

  return null;
}

function looksLikeGroupedRecall(message = '') {
  const lower = normalizeText(message);
  if (!lower) return false;

  const recallLead = /(?:do you|can you|could you|would you|will you|what|who)/i.test(lower);
  const memoryVerb = /(remember|know|recall|tell me about|who are|what are)/i.test(lower);
  const possessive = /\bmy\b/i.test(lower);

  return recallLead && (memoryVerb || possessive);
}

function looksLikeHelpRequest(message = '') {
  const lower = message.toLowerCase().trim();
  return HELP_PATTERNS.some((pattern) => pattern.test(lower));
}

function looksLikeGeneralUserQuestion(message = '') {
  const lower = message.toLowerCase().trim();

  if (!lower) return false;
  if (lower.includes('?')) return true;

  return /^(what|how|why|when|where|who|can you|could you|would you|will you|do you|are you|is it)\b/.test(lower);
}

async function detectIntent(message, shadowId = null) {
  const lower = message.toLowerCase().trim();
  debugLog('🧪 detectIntent running — message:', message, '| lower:', lower);

  let vectorHints = [];
  try {
    if (shadowId) {
      const res = await queryVectorMemory({ text: message, shadowId });
      vectorHints = res?.matches || [];
      debugLog('🧠 Vector memory matches:', vectorHints);
    }
  } catch (err) {
    console.error('❌ Vector memory query failed:', err.message);
  }

  if (LIST_PHRASES.some((p) => lower.includes(p))) return { intent: 'list_create', target: null, vectorHints };
  if (REMINDER_PHRASES.some((p) => lower.includes(p))) return { intent: 'reminder_request', target: null, vectorHints };
  if (PRAYER_PHRASES.some((p) => lower.includes(p))) return { intent: 'prayer_request', target: null, vectorHints };
  if (HUMOR_PHRASES.some((p) => lower.includes(p))) return { intent: 'humor_request', target: null, vectorHints };
  if (AFFIRMATION_PHRASES.some((p) => lower.includes(p))) return { intent: 'affirmation', target: null, vectorHints };
  if (COMFORT_PHRASES.some((p) => lower.includes(p))) return { intent: 'comfort', target: null, vectorHints };
  if (LOSS_PHRASES.some((p) => lower.includes(p))) return { intent: 'loss', target: null, vectorHints };
  if (QUOTE_PHRASES.some((p) => lower.includes(p))) return { intent: 'quote_capture', target: null, vectorHints };
  if (DREAM_PHRASES.some((p) => lower.includes(p))) return { intent: 'dream_note', target: null, vectorHints };
  if (GRATITUDE_MEMORY_PHRASES.some((p) => lower.includes(p))) return { intent: 'gratitude_memory', target: null, vectorHints };
  if (lower.includes('thank')) return { intent: 'gratitude', target: null, vectorHints };

  if (looksLikeHelpRequest(lower)) {
    return { intent: 'help_request', target: null, vectorHints };
  }

  if (lower.startsWith('hello') || lower.startsWith('hi') || lower.startsWith('hey')) {
    return { intent: 'greeting', target: null, vectorHints };
  }

  const negationTerms = ["don't", 'not', "isn't", "wasn't", 'never', 'no intention', 'won’t'];
  const isNegated = negationTerms.some((neg) => lower.includes(neg));
  const isExplicitNap = NAP_PHRASES.some((p) => lower.includes(p));

  if (isExplicitNap && !isNegated) {
    return { intent: 'nap', target: null, vectorHints };
  }

  if (!isTriviaQuestion(lower) && looksLikeGroupedRecall(lower)) {
    const recallTarget = resolveRecallTarget(lower);

    if (recallTarget) {
      debugLog(`📚 Matched grouped/role recall: ${recallTarget.intent} → ${recallTarget.target} (${recallTarget.targetType})`);
      return {
        intent: recallTarget.intent,
        target: recallTarget.target,
        targetType: recallTarget.targetType,
        vectorHints,
      };
    }
  }

if (looksLikeGeneralUserQuestion(lower) && !isTriviaQuestion(lower)) {
  debugLog(
    '🧭 General question detected — allowing fallback classification:',
    message
  );
}

  try {
    debugLog('⚠️ Reached fallback — sending to LLM intent router');

    const memoryData = localStorage.getItem('daisy-memory');
    let context = {};

    if (memoryData) {
      try {
        const parsedMemory = JSON.parse(memoryData);
        if (Array.isArray(parsedMemory.relationships)) {
          context.relationships = parsedMemory.relationships;
          debugLog('📦 Injected context.relationships into fallback:', context.relationships);
        }
      } catch (err) {
        console.warn('❌ Failed to parse memory context:', err.message);
      }
    }

    const response = await axios.post('/api/intent/fallback', {
      message,
      context,
    });

    debugLog('📡 [IntentFallback] Response status:', response.status);
    const result = response.data || {};

    if (
      (result.intent === 'memory_recall' || result.intent === 'recall_request') &&
      isTriviaQuestion(message)
    ) {
      console.warn('[Intent Override] Corrected memory_recall → trivia_question for:', message);
      result.intent = 'trivia_question';
    }

    const softGreetingPhrases = [
      'good so far',
      'so far so good',
      'pretty good this morning',
      'doing okay today',
      'not bad so far',
      'been alright this morning',
      'doing well so far',
      'going fine today',
      'today’s been good',
      'morning’s going fine',
      'so far everything’s fine'
    ];

    if (
      result.intent === 'greeting' &&
      softGreetingPhrases.some((p) => lower.includes(p)) &&
      (result.emotion === 'neutral' || !result.emotion)
    ) {
      console.warn('[Intent Override] Soft follow-up greeting → smalltalk');
      result.intent = 'smalltalk';
    }

    if (result.intent === 'greeting' && looksLikeHelpRequest(lower)) {
      console.warn('[Intent Override] Corrected greeting → help_request for:', message);
      result.intent = 'help_request';
    }

    return { ...result, vectorHints };
  } catch (err) {
    console.error('❌ [IntentFallback] Error:', err.response?.data || err.message);
  }

  return { intent: 'unknown', target: null, vectorHints };
}

function isTriviaQuestion(message = '') {
  const lower = message.toLowerCase().trim();
  const starters = [
    'who is', 'what is', 'when was', 'where is',
    'how many', 'how much', 'what year', 'name the',
    'who won', 'define', 'explain', 'list the', 'give me the'
  ];
  return starters.some((phrase) => lower.startsWith(phrase));
}

function isTriviaAnswer(message = '') {
  return /^[A-Z][a-z]+(?:\s[A-Z][a-z]+)*$/.test(message);
}

export { detectIntent, isTriviaQuestion, isTriviaAnswer };